"use client";

import { useRouter, useSearchParams } from "next/navigation";

import ErrorMessage from "@/app/component/error-message";
import PrimaryButtonGradient from "@/app/component/gradientbutton";
import { InputTextBox } from "@/app/component/InputTextBox";
import MobileSelect from "@/app/component/mobileselect/mobileselectinput";
import ReCaptchaComponent from "@/app/component/recaptcha";
import { useClientConfigProvider } from "@/app/config/client-config-provider";
import { EventLabels } from "@/app/constants/mix-panel-enums";
import AppRoutes from "@/app/constants/routes";
import StringConstants from "@/app/constants/string-contants";
import { useMixPanel } from "@/app/context/mixPanelContext";
import isLoggedIn from "@/app/redirect";
import { CustomToastEnum, useCustomToast } from "@/app/toast/custom-toast";
import ToastUtils from "@/app/toast/toast-utils";
import {
  signInWithApple,
  signInWithFacebookPopup,
  signInWithGooglePopup,
} from "@/app/utils/firebase.utils";
import {
  UniversalStorageConstants,
  UniversalStorageUtils,
  UseQueryResult,
  useAppContext,
  useQuery,
} from "@thriwe-2-0/thriwe-ui-utils";
import { useFormik } from "formik";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import * as Yup from "yup";
import { SignInUI } from "./components/common-signin-ui";
import { OTPPage } from "./components/otp-component";
import { VerifiedComponent } from "./components/verified-ui";

export interface LoginInfo {
  countryCode?: string;
  mobile?: string;
  token?: string;
  email?: string;
  fromWhere: string;
}
export interface CountryCodeType {
  data: any;
  code: string;
  flag: any;
  currency: string;
  name: string;
}

export enum LoginType {
  Mobile,
  Email,
  MobileAndEmail,
}

function LoginWithOTP({
  captcha = false,
  placeHolderText,
  toastType,
  islogin = true,
}: any) {
  const context = useAppContext();
  const { trackEvent } = useMixPanel();
  const { t } = useTranslation();

  const {
    instances: { apiRepository },
    fetchUserProfile,
  } = context;
  const { config, getImagePath } = useClientConfigProvider();
  const router = useRouter();

  const [countryCodesArr, setCountryCodeArr] = useState<CountryCodeType[]>([]);
  const [countryCode, setCountryCode] = useState("");
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tnc, setTnc] = useState<boolean>(
    !config?.login?.termAndConditionCheckbox
  );
  const [isOtpPage, setIsOtpPage] = useState<boolean>(false);
  const [mobileNumber, setMobileNumber] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [detail, setDetails] = useState<LoginInfo>();
  const [isVerified, setIsVerified] = useState<boolean>(false);

  const [switchSignInType, setswitchSignInType] = useState<string>();
  const [validationSchema, setValidationSchema] = useState<any>(null);
  const { renderError, showErrorToast } = useCustomToast(
    toastType ?? CustomToastEnum.Toast
  );

  const searchParams = useSearchParams();
  const benefitGroup = searchParams.get(StringConstants.PARAM_BENEFIT_GROUP);
  const authToken = searchParams
    .get(StringConstants.PARAM_AUTH_TOKEN)
    ?.replaceAll(" ", "+");

  useEffect(() => {
    if (benefitGroup && authToken) {
      apiRepository
        ?.loginRegister({
          config: {
            headers: {
              "X-Auth-Token": authToken,
              "Benefit-Group": benefitGroup,
            },
          },
        })
        .then((v: any) => {
          UniversalStorageUtils.setItem(
            config?.nativeDataSavingType,
            UniversalStorageConstants.AUTH_TOKEN,
            v.headers.authorization
          );

          fetchUserProfile();
          setTimeout(() => {
            router.push(AppRoutes.dashboard);
          }, 500);
        })
        .catch((e: any) => {
          console.log("login failed error", e);
        });
    }
  }, [benefitGroup, authToken]);

  function edit() {
    setDetails({
      fromWhere: "",
      token: "",
      mobile: "",
    });
    setIsOtpPage(false);
  }

  useEffect(() => {
    if (switchSignInType == "email" && islogin) {
      trackEvent(EventLabels.SL_Login_Email, { screen: "Login" });
      trackEvent(EventLabels.SL_Login_Email_Done, { screen: "Login" });
    }
  }, [switchSignInType, trackEvent]);

  useEffect(() => {
    if (!switchSignInType) {
      setswitchSignInType(Object.keys(config?.login?.fields ?? {})?.[0]);
    }
    if (config) {
      if (switchSignInType == "mobile" && config?.login?.fields?.mobile) {
        setValidationSchema(
          Yup.object().shape({
            mobile: Yup.string()
              .required(`${config?.login?.fields?.mobile?.validationText}`)
              .matches(
                config?.login?.fields?.mobile?.validationRegex,
                config?.login?.fields?.mobile?.validationText
              ),
          })
        );
      } else if (switchSignInType == "email" && config?.login?.fields?.email) {
        setValidationSchema(
          Yup.object().shape({
            email: Yup.string()
              .required(
                t("common._isRequiredField", {
                  field: t("common.email", { format: "capitalize" }),
                })
              )
              .matches(
                config?.login?.fields?.email?.validationRegex,
                config?.login?.fields?.email?.validationText
              ),
          })
        );
      }
      (() => signUpForm.validateForm())();
      signUpForm.setFieldValue(
        switchSignInType == "mobile" ? "email" : "mobile",
        ""
      );
    }
  }, [switchSignInType, config]);

  const handleFormSubmit = (values: any) => {
    if (captcha) {
      if (captchaValue === null || captchaValue == "") {
        showErrorToast(t("registerLogin.captchaPrompt"));
        return;
      }
    }
    setIsLoading(true);
    const mobileNumber = values.mobile;
    setEmail(values?.email);
    setMobileNumber(mobileNumber);
    setIsLoading(true);
    apiRepository
      ?.loginRegister({
        email: values?.email,
        phone: values?.mobile.toString(),
        countryCode: countryCode?.toString(),
      })
      .then((v) => {
        setIsLoading(false);
        console.log(v.headers.authorization);
        var token = v.headers.authorization;
        setIsLoading(false);
        let obj: LoginInfo = {
          countryCode: countryCode ?? "",
          mobile: mobileNumber ?? "",
          email: values.email ?? "",
          token: token,
          fromWhere: "1",
        };
        setDetails(obj);
        setIsOtpPage(true);
      })
      .catch((e) => {
        setIsLoading(false);
        console.log("login error response", e?.response?.data);
        if (e?.response?.data?.message) {
          ToastUtils.showErrorToast(
            ["ICICI_V1"]?.includes(
              process.env.NEXT_PUBLIC_PROJECT_CODE ?? ""
            ) && e?.response?.data?.code === 1305
              ? "User Not Registered"
              : e?.response?.data?.message
          );
        } else {
          ToastUtils.showErrorToast(t("common.errorMessage"));
        }
      });
  };
  const item = {
    name: "Mobile Number",
    variableName: "mobile",
    editable: true,
    type: "number",
    labelName: "",
  };
  const signUpForm = useFormik({
    initialValues: {
      mobile: "",
      email: "",
    },
    isInitialValid: false,
    validationSchema: validationSchema,
    onSubmit: handleFormSubmit,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnMount: true,
    validateOnChange: true,
  });

  const {
    setFieldValue,
    values,
    handleBlur,
    handleChange,
    submitForm,
    touched,
    errors,
    isValid,
    validateOnChange,
  } = signUpForm;

  const switchProviderId = (ProviderId: string) => {
    switch (ProviderId) {
      case "google.com":
        return "google";
      case "facebook.com":
        return "facebook";
      case "apple.com":
        return "apple";
    }
  };
  const OauthPass = async (providerCallBack: Function) => {
    try {
      const response = await providerCallBack();
      if (response) {
        let provider = switchProviderId(response.providerId);
        setIsLoading(true);
        apiRepository
          ?.loginRegister({
            email: "",
            phone: "",
            oAuthProvider: provider ?? response?.providerId,
            oAuthToken: await response.user.getIdToken(),
          })
          .then((v: any) => {
            setIsLoading(false);
            setIsVerified(true);

            UniversalStorageUtils.setItem(
              config?.nativeDataSavingType,
              UniversalStorageConstants.AUTH_TOKEN,
              v.headers.authorization
            );

            fetchUserProfile();
            setTimeout(() => {
              router.push(AppRoutes.dashboard);
            }, 1000);
          })
          .catch((e: any) => {
            setIsLoading(false);
            console.log("login failed error", e);
          });
      }
      console.log("google resp", response);
    } catch (e: any) {
      if (e?.code == "auth/account-exists-with-different-credential") {
        ToastUtils.showErrorToast(t("registerLogin.accountExistWithDiffCred"));
      }
      console.log("facebook resp error- ", e?.code);
      console.log("facebook resp error- ", e?.message);
    }
  };

  const convertData = (data: any) => {
    return data.map((item: any, index: number) => ({
      value: item.code,
      label: item.name,
      flag: item?.flag,
      data: item,
    }));
  };

  const useCountriesQuery = (): UseQueryResult<any, Error> =>
    useQuery<any, Error>({
      queryKey: ["countries"],
      queryFn: async () => {
        try {
          const response = await apiRepository?.getCountryCode({});
          return response;
        } catch (e) {
          console.error("Error fetching countries:", e);
          throw new Error("Failed to fetch countries");
        }
      },
      gcTime: 120000,
      staleTime: 120000,
    });

  const {
    data: countriesData,
    error,
    isLoading: isLoadingCountries,
    isError,
    refetch,
  } = useCountriesQuery();

  useEffect(() => {
    if (countriesData?.data) {
      const sortCountriesByName = (countries: CountryCodeType[]) => {
        return countries;
      };
      const sortedCountries = sortCountriesByName(countriesData?.data);
      setCountryCode(sortedCountries?.[0]?.code);
      const convertedData = convertData(sortedCountries);
      setCountryCodeArr(convertedData);
    }
  }, [countriesData]);

  const handleDivClick = (handleFormSubmit: any) => {
    handleFormSubmit();
    trackEvent(islogin ? EventLabels.CTA_LOGIN : EventLabels.CTA_Registration, {
      meta:
        switchSignInType == "email" ? "Login with Email" : "Login with Mobile",
      screen: "Login",
    });
  };

  const loginUI = () => {
    return (
      <div
        className="login-side-container login-size"
        style={{ textAlign: "start", borderRadius: "16px", zIndex: "10" }}
      >
        {isOtpPage ? (
          <OTPPage
            preLoginObject={detail}
            editOnClick={edit}
            switchSignInType={switchSignInType ?? ""}
          />
        ) : !isVerified ? (
          <div className="login-flex-container">
            <SignInUI
              switchSignInType={switchSignInType}
              setswitchSignInType={setswitchSignInType}
              islogin={islogin}
            />

            <div className="input-label-group login-flex-container-item">
              <div className="label" style={{ marginBottom: "0px" }}>
                {switchSignInType === "mobile"
                  ? t("enterYour.phoneNumber")
                  : t("enterYour.emailAddress")}
              </div>

              {switchSignInType == "mobile" &&
                config?.login?.fields?.mobile && (
                  <>
                    <div>
                      <div
                        className="mobile-select"
                        style={{ display: "flex", flexDirection: "row" }}
                      ></div>
                      <MobileSelect
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        formik={signUpForm}
                        countryCode={countryCode}
                        countryCodesArr={countryCodesArr}
                        setCountryCode={setCountryCode}
                        errors={errors}
                        values={values}
                        touched={touched}
                        item={item}
                        hasError={
                          touched?.mobile != null && errors?.mobile != null
                        }
                      />
                      <div style={{ marginTop: "3px" }}>
                        <ErrorMessage
                          message={
                            touched?.mobile && errors?.mobile
                              ? errors?.mobile ?? ""
                              : undefined
                          }
                          touched={touched?.mobile}
                        />
                      </div>
                    </div>
                  </>
                )}

              {switchSignInType == "email" && config?.login?.fields?.email && (
                <div>
                  <InputTextBox
                    hint={placeHolderText}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name="email"
                    value={values.email}
                    placeholder={t("common.emailAddress")}
                    hasError={touched?.email != null && errors?.email != null}
                  />
                  <div style={{ marginTop: "3px" }}>
                    <ErrorMessage
                      message={touched?.email ? errors?.email ?? "" : undefined}
                      touched={touched?.email}
                    />
                  </div>
                </div>
              )}
            </div>

            {config?.login?.disclaimer && (
              <div>
                <div className="termscondition-font disclaimer-icon">
                  <Image
                    className="c-pointer termscondition-checkbox"
                    id="terms&condition"
                    alt=""
                    src={getImagePath("info-landing.png")}
                    width={18}
                    height={18}
                  />
                  <label>{t("config_based.disclaimerLogin")}</label>
                </div>
              </div>
            )}

            {config?.login?.termAndConditionCheckbox && (
              <div>
                <div className="termscondition-font">
                  <Image
                    className="c-pointer termscondition-checkbox"
                    id="terms&condition"
                    alt=""
                    src={getImagePath(
                      !tnc ? "checkbox1.svg" : "checked-checkbox1.svg"
                    )}
                    width={18}
                    height={18}
                    onClick={() => {
                      setTnc(!tnc);
                    }}
                  />
                  <label
                    htmlFor="terms-condition"
                    className="cursor-pointer terms-condition"
                    onClick={() => {
                      setTnc(!tnc);
                    }}
                  >
                    <Trans
                      i18nKey="registerLogin.terms_agreement"
                      components={{
                        1: (
                          <span
                            className="font-bold"
                            onClick={() =>
                              window.open(AppRoutes.termAndCondition, "_blank")
                            }
                          />
                        ),
                        2: (
                          <span
                            className="font-bold"
                            onClick={() =>
                              window.open(AppRoutes.privacyAndPolicy, "_blank")
                            }
                          />
                        ),
                      }}
                    />
                  </label>
                </div>
              </div>
            )}

            {captcha && (
              <div>
                <ReCaptchaComponent
                  onChange={(value) => {
                    setCaptchaValue(value);
                  }}
                />
              </div>
            )}

            {/* {renderError()} */}
            <div className="button-container">
              <PrimaryButtonGradient
                padding="0px !important"
                name={
                  islogin
                    ? config?.login?.loginButton?.text || t("common.signIn")
                    : config?.login?.signupButton?.text || t("common.signUp")
                }
                isDisabled={!isValid || !tnc}
                fullWidth={true}
                onClick={() => handleDivClick(submitForm)}
                isLoading={isLoading}
                classNameDiv="buttonDiv"
                secondary={false}
                arrowIcon={
                  <span>
                    <Image
                      src={getImagePath("arrow-forward.png")}
                      alt=""
                      width="13"
                      height="13"
                      style={{
                        width: "auto",
                        height: "13px",
                      }}
                    />
                  </span>
                }
                classNameBackgroundStylePrimaryGradient="buttonBackgroundStylePrimaryGradient2"
                classNamePrimaryIsLoading="buttonBackgroundStylePrimaryGradient2"
              />
            </div>
            {config?.login?.showauth &&
              config?.login?.authType?.length != 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "10px",
                    alignItems: "center",
                  }}
                >
                  <hr
                    className="hr-login-size"
                    style={{
                      flex: 1,
                      borderColor: "var(--LightTheme-Black-Black) !important",
                    }}
                  />
                  <div className="login-divider-or">
                    <b>{t("common.Or")}</b>
                  </div>
                  <hr
                    className="hr-login-size"
                    style={{
                      flex: 1,
                      borderColor: "var(--LightTheme-Black-Black) !important",
                    }}
                  />
                </div>
              )}
            {config?.login?.showauth && (
              <div className="social-media-login-option-container">
                {config?.login?.authType?.map((item, index) => {
                  return (
                    <div
                      key={index}
                      role="button"
                      onClick={() => {
                        if (item?.type == "google") {
                          trackEvent(
                            islogin
                              ? EventLabels.CTA_LOGIN
                              : EventLabels.CTA_Registration,
                            { meta: "Login with google", screen: "Login" }
                          );
                          return OauthPass(signInWithGooglePopup);
                        }
                        if (item?.type == "apple") {
                          trackEvent(
                            islogin
                              ? EventLabels.CTA_LOGIN
                              : EventLabels.CTA_Registration,
                            { meta: "Login with appleid", screen: "Login" }
                          );

                          return OauthPass(signInWithApple);
                        }
                        if (item?.type == "facebook") {
                          trackEvent(
                            islogin
                              ? EventLabels.CTA_LOGIN
                              : EventLabels.CTA_Registration,
                            { meta: "Login with facebook", screen: "Login" }
                          );
                          return OauthPass(signInWithFacebookPopup);
                        }
                      }}
                      className="social-login-option-btn"
                    >
                      <div>
                        <Image
                          src={getImagePath(`${item?.logo}.png`)}
                          width="34"
                          height="34"
                          alt={"google"}
                          className="oauth-partner-image"
                        />
                      </div>
                      <div className="login-option-text">
                        {t("registerLogin.signinWith") +
                          " " +
                          (item?.type ?? "")}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <VerifiedComponent />
        )}
      </div>
    );
  };

  return loginUI();
}

export default isLoggedIn(LoginWithOTP);
